// SectionRenderer.js — Switch por tipo de sección → componente
import React from 'react';
import styled from 'styled-components';
import { useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import HeroSection from './sections/HeroSection';
import HeroBannerSplit from './sections/HeroBannerSplit';
import StatsSection from './sections/StatsSection';
import FeatureCardsSection from './sections/FeatureCardsSection';
import IconTextGridSection from './sections/IconTextGridSection';
import TextBlockSection from './sections/TextBlockSection';
import ImageGallerySection from './sections/ImageGallerySection';
import CtaButtonSection from './sections/CtaButtonSection';
import TestimonialsSection from './sections/TestimonialsSection';
import CustomHtmlSection from './sections/CustomHtmlSection';
import HeaderSection from './sections/HeaderSection';
import FooterSection from './sections/FooterSection';
import NosotrosStreamingSection from './sections/NosotrosStreamingSection';
import DocumentoLegalSection from './sections/DocumentoLegalSection';
import ContactoFormSection from './sections/ContactoFormSection';
import AccesoStreamingSection from './sections/AccesoStreamingSection';
import ProgresoStreamingSection from './sections/ProgresoStreamingSection';
import ContenidosCarouselSection from './sections/ContenidosCarouselSection';
import ContenidosListaSection from './sections/ContenidosListaSection';
import CursosFichasSection from './sections/CursosFichasSection';
import CursosPorPerfilSection from './sections/CursosPorPerfilSection';
import ProgramaModulosSection from './sections/ProgramaModulosSection';
import DocentesPorPerfilSection from './sections/DocentesPorPerfilSection';
import DocentesSection from './sections/DocentesSection';
import BannerLogoSection from './sections/BannerLogoSection';
import VideoSection from './sections/VideoSection';
import LoginFormSection from './sections/LoginFormSection';
import RegistroFormSection from './sections/RegistroFormSection';
import CursoCompletadoSection from './sections/CursoCompletadoSection';
import EncuestaFormSection from './sections/EncuestaFormSection';
import ProgresoCursosSection from './sections/ProgresoCursosSection';
import MisEntregas from '../entregas/MisEntregas';
import PerfilFormSection from './sections/PerfilFormSection';
import SolicitudAccesoSection from './sections/SolicitudAccesoSection';
import CabeceraPaginaSection from './sections/CabeceraPaginaSection';
import ArticulosSection from './sections/ArticulosSection';
import TarjetasIconoSection from './sections/TarjetasIconoSection';
import AccesoCorreoSection from './sections/AccesoCorreoSection';
import TarjetaProgramaSection from './sections/TarjetaProgramaSection';
import TarjetasCursoSection from './sections/TarjetasCursoSection';
import RecuperarContrasenaSection from './sections/RecuperarContrasenaSection';
import CursoContenidoSection from './sections/CursoContenidoSection';
import CertificadosTablaSection from './sections/CertificadosTablaSection';
import CatalogoStreamingSection from './sections/CatalogoStreamingSection';
import {
  PortadaAccesoSection, TextoVerMasSection, IconosFondoSection, TituloTextoImagenSection,
  ModulosGrillaSection, EstacionesSection, VideosGrillaSection, RegistroTarjetaSection,
  BannerInternoSection, MenuIconosSection, TextoVideosSection, ModulosTarjetasSection,
} from './sections/PortadaCursoSecciones';
import { usePais } from '../../contexts/PaisContext';
import { resolverConfigAudiencia } from './audiencia';
import SeccionPlegable from './SeccionPlegable';
import { corteCss, tieneCorte } from './corteHelpers';
import { seccionVisibleParaSesion } from './visibilidadSesion';

const SECTION_MAP = {
  hero: HeroSection,
  banner_logo: BannerLogoSection,
  video: VideoSection,
  login_form: LoginFormSection,
  registro_form: RegistroFormSection,
  curso_completado: CursoCompletadoSection,
  encuesta_form: EncuestaFormSection,
  progreso_cursos: ProgresoCursosSection,
  mis_entregas: ({ config = {}, tl = (v, d = '') => v || d }) => (
    <MisEntregas titulo={tl(config.titulo, 'Mis entregas')} acento={config.color_acento} vacio={tl(config.texto_vacio, '')} mostrarVacio={config.mostrar_vacio !== false} />
  ),
  perfil_form: PerfilFormSection,
  solicitud_acceso: SolicitudAccesoSection,
  cabecera_pagina: CabeceraPaginaSection,
  articulos: ArticulosSection,
  tarjetas_icono: TarjetasIconoSection,
  acceso_correo: AccesoCorreoSection,
  tarjeta_programa: TarjetaProgramaSection,
  tarjetas_curso: TarjetasCursoSection,
  recuperar_contrasena: RecuperarContrasenaSection,
  curso_contenido: CursoContenidoSection,
  certificados_tabla: CertificadosTablaSection,
  catalogo_streaming: CatalogoStreamingSection,
  portada_acceso: PortadaAccesoSection,
  texto_vermas: TextoVerMasSection,
  iconos_fondo: IconosFondoSection,
  titulo_texto_imagen: TituloTextoImagenSection,
  modulos_grilla: ModulosGrillaSection,
  estaciones: EstacionesSection,
  videos_grilla: VideosGrillaSection,
  registro_tarjeta: RegistroTarjetaSection,
  banner_interno: BannerInternoSection,
  menu_iconos: MenuIconosSection,
  texto_videos: TextoVideosSection,
  modulos_tarjetas: ModulosTarjetasSection,
  nosotros_streaming: NosotrosStreamingSection,
  documento_legal: DocumentoLegalSection,
  contacto_form: ContactoFormSection,
  acceso_streaming: AccesoStreamingSection,
  progreso_streaming: ProgresoStreamingSection,
  stats: StatsSection,
  feature_cards: FeatureCardsSection,
  icon_text_grid: IconTextGridSection,
  text_block: TextBlockSection,
  image_gallery: ImageGallerySection,
  cta_button: CtaButtonSection,
  testimonials: TestimonialsSection,
  custom_html: CustomHtmlSection,
  header: HeaderSection,
  footer: FooterSection,
  contenidos_carousel: ContenidosCarouselSection,
  cursos_fichas: CursosFichasSection,
  cursos_por_perfil: CursosPorPerfilSection,
  programa_modulos: ProgramaModulosSection,
  docentes_por_perfil: DocentesPorPerfilSection,
  contenidos_lista: ContenidosListaSection,
  docentes: DocentesSection,
};

// Variantes de diseño dentro de un mismo tipo de sección. La clave se guarda en
// `config.variante`; si no está o no coincide, se usa el componente por defecto
// del tipo (por eso los proyectos existentes no cambian).
const VARIANTES = {
  hero: {
    banner_split: HeroBannerSplit,
  },
};

// La regla de «para quién se muestra» vive en su propio módulo porque el footer
// global también la necesita. Se reexporta para no romper lo que ya la importa
// desde aquí.
export { seccionVisibleParaSesion };

// Envoltorio para lo que no depende del tipo de sección: el corte de forma
// contra la sección vecina y la visibilidad por dispositivo (`mostrar_en`:
// 'movil' | 'desktop'). Solo se usa cuando la sección pide alguna de las dos
// cosas, así que las landings existentes siguen sin este div de más.
const Envoltorio = styled.div`
  position: relative;
  ${p => p.$corte}
  ${p => p.$soloMovil && '@media (min-width: 981px) { display: none; }'}
  ${p => p.$soloDesktop && '@media (max-width: 980px) { display: none; }'}
`;

export const necesitaEnvoltorio = (c = {}) =>
  tieneCorte(c) || c.mostrar_en === 'movil' || c.mostrar_en === 'desktop';

const SectionRenderer = ({ seccion, proyecto, openLogin, openRegister, tl }) => {
  const { user } = useAuth();
  const { audiencia } = usePais();
  // Páginas con artículos (/hub/:articulo): una sección puede salir solo en el
  // listado o solo dentro de un artículo (`vista`). Sin la clave, en las dos.
  const { articulo } = useParams();
  const variante = seccion.config?.variante;
  const Component = (variante && VARIANTES[seccion.tipo]?.[variante]) || SECTION_MAP[seccion.tipo];
  if (!seccionVisibleParaSesion(seccion.config, user)) return null;
  if (!Component) {
    console.warn(`Tipo de sección desconocido: ${seccion.tipo}`);
    return null;
  }
  // Variantes por país / perfil: sin `variantes_audiencia` devuelve la misma
  // config, así que las secciones existentes se pintan igual que siempre.
  // `desde_portada`: la sección toma la config de la del mismo tipo en la portada
  // y solo cambia lo que traiga escrito (p. ej. /contenidos con el mismo catálogo,
  // header y footer que la home, sin duplicarlos).
  const base = seccion.config?.desde_portada
    ? ((proyecto?.config_json?.landing_secciones || []).find(x => x.tipo === seccion.tipo && x.visible !== false)?.config || {})
    : null;
  const cfg = resolverConfigAudiencia(base ? { ...base, ...seccion.config } : (seccion.config || {}), audiencia);
  // Una variante puede apagar la sección para su país/perfil (`oculta: true`),
  // que es como un mismo bloque existe en unos países y en otros no.
  if (cfg.oculta === true) return null;
  if (cfg.vista === 'listado' && articulo) return null;
  if (cfg.vista === 'articulo' && !articulo) return null;
  let contenido = (
    <Component
      config={cfg}
      proyecto={proyecto}
      openLogin={openLogin}
      openRegister={openRegister}
      tl={tl}
    />
  );
  // Corte de forma contra la sección vecina y/o visibilidad por dispositivo.
  if (necesitaEnvoltorio(cfg)) {
    contenido = (
      <Envoltorio
        $corte={corteCss(cfg)}
        $soloMovil={cfg.mostrar_en === 'movil'}
        $soloDesktop={cfg.mostrar_en === 'desktop'}
      >
        {contenido}
      </Envoltorio>
    );
  }
  // Cualquier sección puede vivir dentro de un acordeón (`plegable`).
  if (cfg.plegable === true) {
    return <SeccionPlegable config={cfg} tl={tl}>{contenido}</SeccionPlegable>;
  }
  return contenido;
};

export default SectionRenderer;
